import { COMPARE, EVENTS } from '../utils/constants';
import {
  readCompareItems,
  writeCompareItems,
  removeCompareItem,
  readCompareMode,
  writeCompareMode
} from '../lib/compareStorage';

// PLP compare (sections/collection.liquid + snippets/product-card.liquid).
// The toolbar switch turns compare mode on, which reveals a Compare button
// on every card and the sticky bar at the foot of the page. Selections are
// capped at COMPARE.MAX_ITEMS and stored via lib/compareStorage; the bar
// links to the compare page (sections/compare.liquid), which renders them.

const PRESSED_ATTR = 'aria-pressed';
const COUNT_TOKEN = '[count]';
const REMOVE_SELECTOR = '[data-compare-remove]';

// Cards arrive uninitialised after filtering / load more, so button state
// is derived from storage on every sync rather than tracked per card
const syncCardButtons = (sectionEl, items) => {
  const isFull = items.length >= COMPARE.MAX_ITEMS;
  sectionEl.querySelectorAll('[data-compare-add]').forEach((buttonEl) => {
    const isSelected = items.some((item) => item.id === buttonEl.dataset.compareId);
    buttonEl.setAttribute(PRESSED_ATTR, String(isSelected));
    buttonEl.disabled = isFull && !isSelected;
  });
};

// Entries carried over from the previous theme have no title or image; when
// their card is on the page, fill them in from it so the bar can show them
const backfillItems = (sectionEl, items) => {
  let hasChanged = false;
  const filledItems = items.map((item) => {
    if (item.title && item.image) return item;
    const buttonEl = sectionEl.querySelector(`[data-compare-add][data-compare-id="${CSS.escape(item.id)}"]`);
    if (!buttonEl) return item;
    hasChanged = true;
    return { ...item, title: buttonEl.dataset.compareTitle, image: buttonEl.dataset.compareImage };
  });
  if (hasChanged) writeCompareItems(filledItems);
  return filledItems;
};

// Built with createElement: the item fields come back out of localStorage
const buildBarItem = (item, removeLabel) => {
  const buttonEl = document.createElement('button');
  buttonEl.type = 'button';
  buttonEl.className = 'relative block size-10 shrink-0 rounded-md bg-white p-1 ring-plum transition-shadow duration-300 hover:ring-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum';
  buttonEl.dataset.compareRemove = item.id;

  if (item.image) {
    const imageEl = document.createElement('img');
    imageEl.src = item.image;
    imageEl.alt = '';
    imageEl.width = 32;
    imageEl.height = 32;
    imageEl.className = 'size-full object-contain';
    buttonEl.append(imageEl);
  }

  const labelEl = document.createElement('span');
  labelEl.className = 'sr-only';
  labelEl.textContent = `${removeLabel} ${item.title || item.handle}`;
  buttonEl.append(labelEl);
  return buttonEl;
};

const syncBar = (barEl, items) => {
  const listEl = barEl.querySelector('[data-compare-items]');
  const promptEl = barEl.querySelector('[data-compare-prompt]');
  const removeAllEl = barEl.querySelector('[data-compare-remove-all]');
  const linkEl = barEl.querySelector('[data-compare-link]');
  const hasItems = items.length > 0;

  if (listEl) {
    const removeLabel = listEl.dataset.removeLabel ?? '';
    listEl.replaceChildren(...items.map((item) => buildBarItem(item, removeLabel)));
  }
  // The prompt doubles as the live region: it restates the cap as a count
  if (promptEl) {
    const { promptText = '', countText = '' } = promptEl.dataset;
    promptEl.textContent = hasItems ? countText.replace(COUNT_TOKEN, String(items.length)) : promptText;
  }
  if (removeAllEl) removeAllEl.hidden = !hasItems;
  if (linkEl) linkEl.hidden = !hasItems;
};

const toggleItem = (buttonEl, items) => {
  const { compareId: id, compareHandle: handle, compareTitle: title, compareImage: image } = buttonEl.dataset;
  const isSelected = items.some((item) => item.id === id);
  if (isSelected) return items.filter((item) => item.id !== id);
  if (items.length >= COMPARE.MAX_ITEMS) return items;
  return [...items, { id, handle, title, image }];
};

// A removed bar thumbnail takes focus with it, so focus moves to the
// thumbnail now in its place (or the last one), else back to the switch
const focusAfterRemove = (barEl, toggleEl, removedIndex) => {
  const thumbEls = barEl.querySelectorAll(REMOVE_SELECTOR);
  const nextEl = thumbEls[Math.min(removedIndex, thumbEls.length - 1)];
  (nextEl ?? toggleEl).focus();
};

const initCompare = () => {
  const sectionEl = document.querySelector('[data-collection-section]');
  if (!sectionEl || sectionEl.dataset.compareInit) return;
  sectionEl.dataset.compareInit = 'true';

  const toggleEl = sectionEl.querySelector('[data-compare-toggle]');
  const barEl = sectionEl.querySelector('[data-compare-bar]');
  if (!toggleEl) return;

  const sync = (items) => {
    const filledItems = backfillItems(sectionEl, items);
    syncCardButtons(sectionEl, filledItems);
    if (barEl) syncBar(barEl, filledItems);
  };

  const setCompareMode = (isActive) => {
    toggleEl.setAttribute('aria-checked', String(isActive));
    sectionEl.toggleAttribute('data-compare-active', isActive);
    writeCompareMode(isActive);
  };

  toggleEl.addEventListener('click', () => {
    setCompareMode(toggleEl.getAttribute('aria-checked') !== 'true');
  });

  // Delegated: card buttons are replaced by filtering and load more, and the
  // bar's remove buttons are rebuilt on every sync
  sectionEl.addEventListener('click', (event) => {
    const addEl = event.target.closest('[data-compare-add]');
    if (addEl) {
      const items = toggleItem(addEl, readCompareItems());
      writeCompareItems(items);
      sync(items);
      return;
    }
    const removeEl = event.target.closest(REMOVE_SELECTOR);
    if (removeEl && barEl) {
      const removedIndex = [...barEl.querySelectorAll(REMOVE_SELECTOR)].indexOf(removeEl);
      sync(removeCompareItem(removeEl.dataset.compareRemove));
      focusAfterRemove(barEl, toggleEl, removedIndex);
      return;
    }
    if (event.target.closest('[data-compare-remove-all]')) {
      // The button hides itself, so hand focus to the switch first
      toggleEl.focus();
      writeCompareItems([]);
      sync([]);
    }
  });

  document.addEventListener(EVENTS.COLLECTION_RERENDER, () => sync(readCompareItems()));

  setCompareMode(readCompareMode());
  sync(readCompareItems());
};

export default initCompare;
